// Avatar ASCII banner — gradient màu cam → tím để in ở các entry point chính:
// `avatar --version`, `avatar init`, `avatar login`.
//
// Style: ANSI Shadow block characters (Unicode box-drawing).
// Tự fallback sang plain text khi không phải TTY (CI, pipe ra file).
import chalk from "chalk";

// 6 dòng chính của logo, mỗi dòng giữ độ rộng đồng nhất 50 ký tự để gradient đẹp.
const BANNER_LINES: readonly string[] = [
  " █████╗ ██╗   ██╗ █████╗ ████████╗ █████╗ ██████╗ ",
  "██╔══██╗██║   ██║██╔══██╗╚══██╔══╝██╔══██╗██╔══██╗",
  "███████║██║   ██║███████║   ██║   ███████║██████╔╝",
  "██╔══██║╚██╗ ██╔╝██╔══██║   ██║   ██╔══██║██╔══██╗",
  "██║  ██║ ╚████╔╝ ██║  ██║   ██║   ██║  ██║██║  ██║",
  "╚═╝  ╚═╝  ╚═══╝  ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝",
] as const;

// Gradient stops cam → tím (RGB triples). Mỗi dòng nội suy theo % chiều cao.
const GRADIENT_STOPS: ReadonlyArray<readonly [number, number, number]> = [
  [217, 79, 30], // cam-cháy (#d94f1e)
  [200, 70, 80], // cam-hồng
  [170, 70, 140], // hồng-tím
  [125, 88, 217], // tím (#7d58d9)
];

// Nội suy tuyến tính 1 kênh màu giữa 2 stop.
function lerpChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

// Lấy màu RGB tại vị trí [0,1] trên gradient.
function gradientAt(t: number): readonly [number, number, number] {
  const clamped = Math.max(0, Math.min(1, t));
  const scaled = clamped * (GRADIENT_STOPS.length - 1);
  const lo = Math.floor(scaled);
  const hi = Math.min(GRADIENT_STOPS.length - 1, lo + 1);
  const localT = scaled - lo;
  // Non-null safe: lo ∈ [0, len-1] qua Math.floor + clamp, hi ∈ [0, len-1] qua Math.min.
  // TS strict mode không narrow tuple index → assert.
  const a = GRADIENT_STOPS[lo]!;
  const b = GRADIENT_STOPS[hi]!;
  return [
    lerpChannel(a[0], b[0], localT),
    lerpChannel(a[1], b[1], localT),
    lerpChannel(a[2], b[2], localT),
  ];
}

// Build banner string đã tô màu sẵn. Trả empty khi terminal không hỗ trợ màu.
export function renderAvatarBanner(opts?: { tagline?: string }): string {
  const isTty = process.stdout.isTTY ?? false;
  const supportsColor = isTty && chalk.level > 0;

  // Plain fallback cho CI hoặc pipe.
  if (!supportsColor) {
    return [...BANNER_LINES, ...(opts?.tagline ? ["", opts.tagline] : [])].join("\n");
  }

  const colored = BANNER_LINES.map((line, idx) => {
    const t = BANNER_LINES.length === 1 ? 0 : idx / (BANNER_LINES.length - 1);
    const [r, g, b] = gradientAt(t);
    return chalk.rgb(r, g, b).bold(line);
  });

  if (opts?.tagline) {
    colored.push("");
    colored.push(chalk.dim(opts.tagline));
  }

  return colored.join("\n");
}

// Tiện ích: in banner ra stdout với newline phía trước/sau cho thoáng.
export function printAvatarBanner(opts?: { tagline?: string }): void {
  process.stdout.write(`\n${renderAvatarBanner(opts)}\n\n`);
}
